import RAPIER from '@dimforge/rapier2d-compat';
import { XY } from './V2.js';

//2D Rapier wrapper for Twrk, sibling of Rapr.ts (3D). Centralizes convention conversion:
//Twrk has flipped Y (negative = up), Rapier2D is Y-up. That mirror also flips angles:
//twrk angle = -rapier angle. All Twrk→Rapier crossings go through the converters below;
//never convert at call sites. Joint limits/motor targets are given in twrk angles.

let ready: Promise<void> | null = null;

const to_rapier_vec = (v: XY): XY => ({ x: v.x, y: -v.y });
const to_twrk_vec = (v: XY): XY => ({ x: v.x, y: -v.y });

export type rapr2_body_t = ReturnType<typeof wrap_body>;
export type rapr2_world_t = ReturnType<typeof make_world>;

export type rapr2_joint_t = {
    raw: RAPIER.ImpulseJoint;
    kill: () => void;
};

export type rapr2_revolute_t = rapr2_joint_t & {
    //motor drives the joint towards a target angle (twrk radians, 0 = spawn pose)
    set_motor: (target: number, stiffness: number, damping?: number) => void;
    set_limits: (min: number, max: number) => void;
};

function wrap_body(world: RAPIER.World, rb: RAPIER.RigidBody) {
    const pos: XY = { x: 0, y: 0 };
    const vel: XY = { x: 0, y: 0 };
    //axis: twrk-space direction of the body's local +Y, i.e. from center towards its "top"
    //end (twrk-up at angle 0). endpoint = pos ± axis * half_height.
    const axis: XY = { x: 0, y: -1 };
    const self = {
        raw: rb,
        pos, vel, axis,
        angle: 0, //twrk radians
        ang_vel: 0,
        sync_read() {
            const rp = rb.translation();
            const rv = rb.linvel();
            const r = rb.rotation();
            pos.x = rp.x; pos.y = -rp.y;
            vel.x = rv.x; vel.y = -rv.y;
            self.angle = -r;
            self.ang_vel = -rb.angvel();
            axis.x = -Math.sin(r);
            axis.y = -Math.cos(r);
        },
        set_pos(v: XY) { rb.setTranslation(to_rapier_vec(v), true); },
        set_angle(a: number) { rb.setRotation(-a, true); },
        set_vel(v: XY) { rb.setLinvel(to_rapier_vec(v), true); },
        set_ang_vel(a: number) { rb.setAngvel(-a, true); },
        impulse(v: XY) { rb.applyImpulse(to_rapier_vec(v), true); },
        torque_impulse(t: number) { rb.applyTorqueImpulse(-t, true); },
        set_gravity_scale(s: number) { rb.setGravityScale(s, true); },
        set_fixed(fixed: boolean) { rb.setBodyType(fixed ? RAPIER.RigidBodyType.Fixed : RAPIER.RigidBodyType.Dynamic, true); },
        set_linear_damping(d: number) { rb.setLinearDamping(d); },
        set_angular_damping(d: number) { rb.setAngularDamping(d); },
        lock_rotation(free: boolean) { rb.lockRotations(!free, true); },
        kill() { world.removeRigidBody(rb); },
    };
    self.sync_read();
    rb.userData = self;
    return self;
}

type body_io = {
    pos: XY;
    angle?: number; //twrk radians
    density?: number;
    friction?: number;
    restitution?: number;
    angular_damp?: number;
    velocity_damp?: number;
    groups?: number; //Rapier interaction-group bitmask, see Rapr2.groups()
    mass?: number; //absolute mass override; takes precedence over density if given
};

type capsule_io = body_io & {
    radius: number; //half-extent == Rapier radius
    half_height: number; //cylindrical-section half-height, capsule runs along local Y
};

type box_io = body_io & {
    half: XY; //half-extents
};

type world_io = { gravity?: number, dt?: number };

function make_world(io: world_io = {}) {
    //positive twrk gravity pulls down (+y twrk) which is -y in Rapier space.
    const g = io.gravity ?? 9.81;
    const dt = io.dt ?? 1 / 60;
    const world = new RAPIER.World({ x: 0, y: -g });
    world.timestep = dt;

    let accum = 0;

    const make_rb = (io: body_io, fixed = false) => {
        const p = to_rapier_vec(io.pos);
        const bd = (fixed ? RAPIER.RigidBodyDesc.fixed() : RAPIER.RigidBodyDesc.dynamic())
            .setTranslation(p.x, p.y)
            .setRotation(-(io.angle ?? 0))
            .setLinearDamping(io.velocity_damp ?? 0.1)
            .setAngularDamping(io.angular_damp ?? 0.2);
        return world.createRigidBody(bd);
    };

    const dress_collider = (cd: RAPIER.ColliderDesc, io: body_io) => {
        //mass wins over density when both are given: setMass fixes the point-mass total while still
        //deriving angular inertia from the collider shape, so limbs of different sizes can share a mass.
        if (io.mass !== undefined) cd.setMass(io.mass);
        else cd.setDensity(io.density ?? 1);
        cd.setFriction(io.friction ?? 0.6)
            .setRestitution(io.restitution ?? 0.05);
        if (io.groups !== undefined) cd.setCollisionGroups(io.groups);
        return cd;
    };

    return {
        raw: world,
        //fixed-timestep accumulator: keeps rolling stable across render dt jitter
        step(frame_dt: number) {
            accum += frame_dt;
            let iters = 0;
            while (accum >= dt && iters < 5) {
                world.step();
                accum -= dt;
                iters++;
            }
        },
        capsule(io: capsule_io): rapr2_body_t {
            const rb = make_rb(io);
            world.createCollider(dress_collider(RAPIER.ColliderDesc.capsule(io.half_height, io.radius), io), rb);
            return wrap_body(world, rb);
        },
        box(io: box_io): rapr2_body_t {
            const rb = make_rb(io);
            world.createCollider(dress_collider(RAPIER.ColliderDesc.cuboid(io.half.x, io.half.y), io), rb);
            return wrap_body(world, rb);
        },
        ball(io: body_io & { radius: number }): rapr2_body_t {
            const rb = make_rb(io);
            world.createCollider(dress_collider(RAPIER.ColliderDesc.ball(io.radius), io), rb);
            return wrap_body(world, rb);
        },
        //fixed anchor body without collider (hook, pivot)
        fixed_point(pos: XY): rapr2_body_t {
            return wrap_body(world, make_rb({ pos }, true));
        },
        //kinematic point for drag targets: external code moves it via set_pos
        kinematic_point(pos: XY): rapr2_body_t {
            const p = to_rapier_vec(pos);
            const bd = RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(p.x, p.y);
            return wrap_body(world, world.createRigidBody(bd));
        },
        //static box collider — fixed in world, no dynamics (floor, walls)
        fixed_box(io: { pos: XY, angle?: number, half: XY, friction?: number, restitution?: number, groups?: number }): rapr2_body_t {
            const rb = make_rb(io, true);
            world.createCollider(dress_collider(RAPIER.ColliderDesc.cuboid(io.half.x, io.half.y), io), rb);
            return wrap_body(world, rb);
        },
        //hinge between two bodies. Anchors are local-frame points (twrk space). The shared spawn
        //angle is the joint's zero; motor target / limits are twrk radians relative to that.
        revolute(io: {
            a: rapr2_body_t, b: rapr2_body_t,
            anchor_a?: XY, anchor_b?: XY,
            limits?: [number, number],
            motor?: { target?: number, stiffness: number, damping?: number },
        }): rapr2_revolute_t {
            const aa = to_rapier_vec(io.anchor_a ?? { x: 0, y: 0 });
            const ab = to_rapier_vec(io.anchor_b ?? { x: 0, y: 0 });
            const data = RAPIER.JointData.revolute(aa, ab);
            const j = world.createImpulseJoint(data, io.a.raw, io.b.raw, true) as RAPIER.RevoluteImpulseJoint;
            //force-based motors: stiffness = torque per radian of error. The default acceleration-based
            //model normalizes by mass and never reaches its target against gravity on light bodies.
            j.configureMotorModel(RAPIER.MotorModel.ForceBased);
            const self: rapr2_revolute_t = {
                raw: j,
                set_motor(target: number, stiffness: number, damping = stiffness * 0.1) {
                    j.configureMotorPosition(-target, stiffness, damping);
                },
                set_limits(min: number, max: number) {
                    j.setLimits(-max, -min); //twrk angle = -rapier angle, so the interval flips
                },
                kill: () => world.removeImpulseJoint(j, true),
            };
            if (io.limits) self.set_limits(io.limits[0], io.limits[1]);
            if (io.motor) self.set_motor(io.motor.target ?? 0, io.motor.stiffness, io.motor.damping);
            return self;
        },
        //rope: keeps two anchors within `length`, no pushback (free slack)
        rope(io: { a: rapr2_body_t, b: rapr2_body_t, length: number, anchor_a?: XY, anchor_b?: XY }): rapr2_joint_t {
            const aa = to_rapier_vec(io.anchor_a ?? { x: 0, y: 0 });
            const ab = to_rapier_vec(io.anchor_b ?? { x: 0, y: 0 });
            const data = RAPIER.JointData.rope(io.length, aa, ab);
            const j = world.createImpulseJoint(data, io.a.raw, io.b.raw, true);
            return { raw: j, kill: () => world.removeImpulseJoint(j, true) };
        },
        //distance spring between two bodies
        spring(io: {
            a: rapr2_body_t, b: rapr2_body_t,
            rest_length?: number, stiffness?: number, damping?: number,
            anchor_a?: XY, anchor_b?: XY,
        }): rapr2_joint_t {
            const aa = to_rapier_vec(io.anchor_a ?? { x: 0, y: 0 });
            const ab = to_rapier_vec(io.anchor_b ?? { x: 0, y: 0 });
            const data = RAPIER.JointData.spring(io.rest_length ?? 0, io.stiffness ?? 50, io.damping ?? 2, aa, ab);
            const j = world.createImpulseJoint(data, io.a.raw, io.b.raw, true);
            return { raw: j, kill: () => world.removeImpulseJoint(j, true) };
        },
        has_active_bodies(): boolean {
            let any = false;
            world.forEachActiveRigidBody(() => { any = true; });
            return any;
        },
        kill() { world.free(); },
    };
}

export const Rapr2 = {
    init(): Promise<void> {
        if (!ready) ready = RAPIER.init();
        return ready;
    },
    to_rapier: { vec: to_rapier_vec },
    to_twrk: { vec: to_twrk_vec },
    world: make_world,
    //pack a Rapier interaction-group mask (membership<<16 | filter). `member` = layers this
    //collider belongs to; `collides` = layers it interacts with.
    groups: (member: number, collides: number): number => ((member & 0xffff) << 16) | (collides & 0xffff),
};
